import React from 'react';
import { Phone, Mail, FileText, MessageCircle, Clock, Users } from 'lucide-react';

const Support: React.FC = () => {
  const handleEmailSupport = () => {
    window.location.href = 'mailto:info@spottsapp.com?subject=Vendor Support Request';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Support & Help</h2>
          <p className="text-gray-600">Get help from our support team</p>
        </div>
        <button
          onClick={handleEmailSupport}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Mail className="h-4 w-4" />
          <span>Contact Support</span>
        </button>
      </div>

      {/* Quick Help */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <a
          href="tel:+2349160244454"
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
        >
          <div className="flex items-center mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Phone className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 ml-3">Phone Support</h3>
          </div>
          <p className="text-gray-600 mb-4">Speak directly with our support team</p>
          <p className="text-lg font-semibold text-gray-900">+234 916 024 4454</p>
          <div className="flex items-center text-sm text-gray-500 mt-2">
            <Clock className="h-4 w-4 mr-1" />
            <span>Mon-Fri: 9 AM - 6 PM WAT</span>
          </div>
        </a>

        <a
          href="mailto:info@spottsapp.com"
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:border-green-300 hover:shadow-md transition-all cursor-pointer"
        >
          <div className="flex items-center mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <Mail className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 ml-3">Email Support</h3>
          </div>
          <p className="text-gray-600 mb-4">Send us detailed questions</p>
          <p className="text-lg font-semibold text-gray-900">info@spottsapp.com</p>
          <div className="flex items-center text-sm text-gray-500 mt-2">
            <Clock className="h-4 w-4 mr-1" />
            <span>Response within 24 hours</span>
          </div>
        </a>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <MessageCircle className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 ml-3">WhatsApp</h3>
          </div>
          <p className="text-gray-600 mb-4">Quick responses via WhatsApp</p>
          <a
            href="https://wa.me/2349160244454"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium"
          >
            Chat Now →
          </a>
        </div>
      </div>

      {/* Common Issues */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Common Issues & Quick Fixes</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
            <h4 className="font-semibold text-gray-900 mb-2">Payment Issues</h4>
            <p className="text-sm text-gray-600 mb-3">
              If customers report payment failures, contact us via WhatsApp at{' '}
              <a href="https://wa.me/2349160244454" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                +234 916 024 4454
              </a>
            </p>
          </div>

          <div className="p-4 bg-green-50 rounded-lg border border-green-100">
            <h4 className="font-semibold text-gray-900 mb-2">Booking Conflicts</h4>
            <p className="text-sm text-gray-600 mb-3">
              Use the Block Time Slot feature in Bookings to prevent conflicts, or manage court availability in Courts/Fields.
            </p>
          </div>

          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
            <h4 className="font-semibold text-gray-900 mb-2">Payout Questions</h4>
            <p className="text-sm text-gray-600 mb-3">
              View your payout schedule and eligible bookings in the Payouts tab. Contact support for bank detail changes.
            </p>
          </div>

          <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
            <h4 className="font-semibold text-gray-900 mb-2">Manual Bookings</h4>
            <p className="text-sm text-gray-600 mb-3">
              Record walk-in or phone bookings using the "Add Manual Booking" button in the Bookings tab.
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-6">
          <FileText className="h-6 w-6 text-gray-700 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Frequently Asked Questions</h3>
        </div>
        <div className="space-y-4">
          {[
            {
              category: 'Venue Management',
              question: 'How do I update my venue pricing?',
              answer: 'Go to Venue Info → Pricing & Discounts section to edit your hourly rates, discounts, and special offers. Changes take effect immediately.',
            },
            {
              category: 'Venue Management',
              question: 'How can I add or remove courts/fields?',
              answer: 'Navigate to Courts/Fields Management page. Click "Add New Court" to create one, or click the edit/delete icons on existing courts to modify them.',
            },
            {
              category: 'Venue Management',
              question: 'How do I set operating hours?',
              answer: 'In Venue Info → Operating Hours, set your opening and closing times for each day. Mark days as "Closed" if your venue is not open that day.',
            },
            {
              category: 'Bookings',
              question: 'Can I cancel a customer booking?',
              answer: 'Yes. Go to Bookings Management, find the booking, and click "Cancel". Refunds are processed automatically based on your cancellation policy (full refund if 24+ hours before booking).',
            },
            {
              category: 'Bookings',
              question: 'How do I handle recurring bookings?',
              answer: 'Recurring bookings appear in your Bookings Management page with a "Recurring" tag. You can view all instances and cancel individual sessions or the entire series.',
            },
            {
              category: 'Bookings',
              question: 'How can I block time slots for maintenance?',
              answer: 'Go to Bookings tab and click "Block Time Slot". Select the court, date range, and time. You can block single or multiple days at once.',
            },
            {
              category: 'Bookings',
              question: 'How do I add a walk-in or phone booking?',
              answer: 'Go to Bookings tab and click "Add Manual Booking". You can record cash, transfer, or POS payments for walk-ins and phone bookings.',
            },
            {
              category: 'Payments & Revenue',
              question: 'When do I receive my payouts?',
              answer: 'Payouts are processed weekly. View your available balance, held bookings, and payout schedule in the Payouts tab.',
            },
            {
              category: 'Payments & Revenue',
              question: 'Why is my payout on hold?',
              answer: 'Bookings are held for a period after the session date to allow for refund requests. Once the hold period passes, funds become available for payout.',
            },
            {
              category: 'Payments & Revenue',
              question: 'How do I update my bank account details?',
              answer: 'For security reasons, bank account changes must be approved by administrators. Contact info@spottsapp.com with your new account details.',
            },
            {
              category: 'Reviews & Ratings',
              question: 'How do I respond to customer reviews?',
              answer: 'Go to Reviews page, find the review, and click "Reply". Your response will be visible to all customers viewing your venue. Be professional and address concerns constructively.',
            },
            {
              category: 'Reviews & Ratings',
              question: 'Can I delete negative reviews?',
              answer: 'You cannot delete customer reviews, but you can reply to address concerns. If a review violates our policies (spam, offensive language), contact support to report it.',
            },
            {
              category: 'Customer Support',
              question: 'How do I handle customer complaints?',
              answer: 'Address complaints professionally through review replies. For serious issues, contact our support team to mediate. We prioritize customer satisfaction and fair resolutions.',
            },
            {
              category: 'Technical Issues',
              question: 'What if I forget my password?',
              answer: 'Click "Forgot Password" on the login page. Enter your email to receive a 6-digit verification code, then set a new password. For assistance, contact info@spottsapp.com.',
            },
            {
              category: 'Technical Issues',
              question: 'The dashboard is loading slowly. What should I do?',
              answer: 'Try refreshing the page or clearing your browser cache. If the issue persists, contact our technical support team with details about your browser and internet connection.',
            },
            {
              category: 'Analytics',
              question: 'How can I see my revenue reports?',
              answer: 'Visit the Revenue tab for detailed earnings breakdown, charts showing booking sources, payment methods, and revenue trends over time.',
            },
            {
              category: 'Account Management',
              question: 'How do I update my contact information?',
              answer: 'Go to Settings → Profile to update your name, email, and phone number. Changes are saved immediately and reflected across the platform.',
            },
            {
              category: 'Account Management',
              question: 'Can I manage multiple venues?',
              answer: 'Currently, each vendor account is linked to one venue. To manage multiple venues, contact support about setting up additional accounts or multi-venue access.',
            },
          ].map((faq, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
                    Q
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      {faq.category}
                    </span>
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{faq.question}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Still Need Help */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg p-8 text-center">
        <Users className="h-12 w-12 text-white mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-white mb-2">Still Need Help?</h3>
        <p className="text-blue-100 mb-6">
          Our support team is here to help you succeed. We typically respond within 24 hours.
        </p>
        <button
          onClick={handleEmailSupport}
          className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors inline-flex items-center"
        >
          <Mail className="h-5 w-5 mr-2" />
          Contact Support Team
        </button>
      </div>
    </div>
  );
};

export default Support;